import { useEffect, useMemo, useState } from "react"
import { useIntl } from "react-intl"

import { SearchInput, StaffLabelTextM } from "@doktor-se/bones-ui"
import { Loader } from "@doktor-se/bones-ui/dist/web-shared/components"

import { createLoadingSelector } from "api"
import { useAppDispatch, useAppSelector } from "lib/hooks"
import { setOverviewPageFilter } from "reducers/app"
import {
  reassignRequestsSelectors,
  selectBookedAppointmentsRequests,
  selectClinicConversations,
  selectClinicErrands,
  setSearchFilter,
  showBookedAppointmentsNotification,
  showMyErrandsNotification
} from "reducers/conversations/conversations.reducer"
import { selectClinicQueues } from "reducers/queue/queue.reducer"

import AssignedConversationList from "./components/AssignedConversationList"
import BookedAppointmentsList from "./components/BookedAppointmentsList"
import FilterButton from "./components/FilterButton"
import OngoingConversationList from "./components/OngoingConversationList"
import QueueConversationList from "./components/QueueConversationList"
import QueueList from "./components/QueueList"

import ErrandNavigationWatcher from "./ErrandNavigationWatcher"

import styles from "./OverviewPage.module.scss"

const loadingSelector = createLoadingSelector(["ASSIGNED_CONVERSATIONS", "QUEUE_COUNT"])

const OverviewPage = () => {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const queues = useAppSelector(selectClinicQueues)
  const loader = useAppSelector(state => loadingSelector(state))
  const { active, booked } = useAppSelector(selectClinicConversations)
  const { bookedAppointments, ongoingErrands, bookedErrands } = useAppSelector(selectClinicErrands)
  const { ongoingRequests, futureRequests } = useAppSelector(selectBookedAppointmentsRequests)
  const activeFilter = useAppSelector(state => state.app.overviewPageFilter)
  const [searchString, setSearchString] = useState("")
  const requests = useAppSelector(reassignRequestsSelectors.selectAll)

  const myErrandsNotification = useAppSelector(showMyErrandsNotification)
  const bookedAppointmentsNotification = useAppSelector(showBookedAppointmentsNotification)

  useEffect(() => {
    dispatch(setSearchFilter(searchString.replace(/-/g, "")))
  }, [searchString, dispatch])

  const setActiveFilter = (filter: string) => {
    dispatch(setOverviewPageFilter(filter))
  }

  const selectedQueue = useMemo(() => queues.find(q => q.queueKey === activeFilter), [activeFilter, queues])

  const renderListContent = () => {
    if (activeFilter === "MY_ERRANDS_FILTER") {
      return <AssignedConversationList requests={requests} active={active} booked={booked} />
    }
    if (activeFilter === "ONGOING_ERRANDS_FILTER") {
      return <OngoingConversationList ongoing={ongoingErrands} booked={bookedErrands} requests={ongoingRequests} />
    }
    if (activeFilter === "BOOKED_APPOINTMENTS_FILTER") {
      return <BookedAppointmentsList conversations={bookedAppointments} requests={futureRequests} />
    }
    return <QueueConversationList selectedQueue={selectedQueue} selectedQueueKey={activeFilter} />
  }

  if (loader) {
    return <Loader type="small" />
  }

  return (
    <div className={styles.container}>
      <ErrandNavigationWatcher />
      <div className={styles.stickyHeader}>
        <div className={styles.header}>
          <h1 className={styles.title}>{intl.formatMessage({ id: "label.inbox" })}</h1>
          <SearchInput
            variant="onBackground"
            value={searchString}
            onChange={value => setSearchString(value)}
            aria-label={intl.formatMessage({ id: "input.search.patient.aria.label" })}
          />
        </div>
        <QueueList queues={queues} activeFilter={activeFilter} onClick={setActiveFilter}>
          <FilterButton
            active={activeFilter === "BOOKED_APPOINTMENTS_FILTER"}
            filter={"BOOKED_APPOINTMENTS"}
            notification={bookedAppointmentsNotification}
            label={`${intl.formatMessage({ id: "label.overview.booked_appointments" })} (${(bookedAppointments?.length || 0) + (futureRequests?.length || 0)})`}
            onClick={() => setActiveFilter("BOOKED_APPOINTMENTS_FILTER")}
          />
        </QueueList>
        <div>
          <StaffLabelTextM style={{ marginBottom: "8px", marginTop: "24px" }}>
            {intl.formatMessage({ id: "label.overview.active_errands" })}
          </StaffLabelTextM>
          <div className={styles.activeFilter}>
            <FilterButton
              filter={"MY_ERRANDS_FILTER"}
              active={activeFilter === "MY_ERRANDS_FILTER"}
              label={intl.formatMessage(
                { id: "label.overview.my_errands_filter" },
                { value: (active.length || 0) + (booked.length || 0) + (requests.length || 0) }
              )}
              notification={myErrandsNotification}
              onClick={() => setActiveFilter("MY_ERRANDS_FILTER")}
            />
            <FilterButton
              filter={"ONGOING_ERRANDS_FILTER"}
              active={activeFilter === "ONGOING_ERRANDS_FILTER"}
              label={`${intl.formatMessage({ id: "label.overview.ongoing_errands" })} (${(ongoingErrands.length || 0) + (bookedErrands.length || 0) + (ongoingRequests.length || 0)})`}
              onClick={() => setActiveFilter("ONGOING_ERRANDS_FILTER")}
            />
          </div>
        </div>
      </div>
      <div>{renderListContent()}</div>
    </div>
  )
}

export default OverviewPage
